import { useEffect, useRef, useState } from "react";
import { Badge, Drawer, Layout, Tooltip } from "antd";
import {
  ApartmentOutlined,
  BellOutlined,
  BulbOutlined,
  CloudDownloadOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  FileTextOutlined,
  FunctionOutlined,
  HistoryOutlined,
  LogoutOutlined,
  MenuOutlined,
  MoonOutlined,
  NodeIndexOutlined,
  SettingOutlined,
  ShareAltOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useTheme, type ThemeMode } from "./contexts/ThemeContext";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import Dashboard      from "./pages/Dashboard";
import SensorDetail   from "./pages/SensorDetail";
import SFlowSources   from "./pages/SFlowSources";
import GraphsStorage  from "./pages/GraphsStorage";
import UserManagement from "./pages/UserManagement";
import Notifications  from "./pages/Notifications";
import Flows          from "./pages/Flows";
import FlowsArchive   from "./pages/FlowsArchive";
import Login          from "./pages/Login";
import ZoneEditor     from "./pages/ZoneEditor";
import IPAddresses    from "./pages/IPAddresses";
import IPTop          from "./pages/IPTop";
import Settings       from "./pages/Settings";
import Decoders       from "./pages/Decoders";
import Reports        from "./pages/Reports";
import ReportHistory  from "./pages/ReportHistory";
import { AuthProvider, useAuth, useRole } from "./contexts/AuthContext";
import { IPZoneProvider } from "./contexts/IPZoneContext";
import SidebarIPBrowser from "./components/SidebarIPBrowser";
import RequireAuth    from "./components/RequireAuth";
import { LicenseGate } from "./pages/LicenseWall";
import { api }        from "./api";

// ── sidebar nav config ────────────────────────────────────────────────────

interface NavItem {
  key:      string;
  icon:     React.ReactNode;
  label:    string;
  to:       string;
  disabled?: boolean;
  // Minimum role required to see this item. Omitted = visible to everyone
  // (readonly+). "operator" = operator/admin; "admin" = admin only.
  minRole?: "operator" | "admin";
}

interface NavSection {
  title:    string;
  items:    NavItem[];
}

const NAV: NavSection[] = [
  {
    title: "Reports",
    items: [
      { key: "/",      icon: <DashboardOutlined />,    label: "Sensors Dashboard", to: "/"      },
      { key: "/top",   icon: <ThunderboltOutlined />,  label: "Top IPs",           to: "/top"   },
      { key: "/flows", icon: <ShareAltOutlined />,     label: "Live Flows",        to: "/flows" },
      { key: "/flows/search", icon: <ShareAltOutlined />, label: "Flow Archive Search", to: "/flows/search" },
      { key: "/reports",         icon: <FileTextOutlined />,  label: "Daily Reports",   to: "/reports",         minRole: "operator" as const },
      { key: "/report-history",  icon: <HistoryOutlined />,   label: "Report History",  to: "/report-history",  minRole: "operator" as const },
    ],
  },
  {
    title: "Network",
    items: [
      { key: "/sensors",  icon: <NodeIndexOutlined />, label: "sFlow Sources", to: "/sensors"  },
      { key: "/zones",    icon: <ApartmentOutlined />, label: "IP Zones",      to: "/zones"    },
      { key: "/decoders", icon: <FunctionOutlined />,  label: "Decoders",      to: "/decoders" },
    ],
  },
  {
    title: "Settings",
    items: [
      { key: "/storage",       icon: <DatabaseOutlined />, label: "Graphs & Storage",  to: "/storage",       minRole: "operator" },
      { key: "/users",         icon: <UserOutlined />,     label: "User Management",   to: "/users",         minRole: "admin"    },
      { key: "/notifications", icon: <BellOutlined />,     label: "Notifications",     to: "/notifications", minRole: "operator" },
      { key: "/settings",      icon: <SettingOutlined />,  label: "Settings & Updates", to: "/settings",     minRole: "admin"    },
    ],
  },
];

function activeKey(pathname: string): string {
  if (pathname === "/") return "/";
  if (pathname.startsWith("/sensors"))       return "/sensors";
  if (pathname.startsWith("/zones"))         return "/zones";
  if (pathname.startsWith("/decoders"))      return "/decoders";
  if (pathname.startsWith("/flows/search"))  return "/flows/search";
  if (pathname.startsWith("/flows"))         return "/flows";
  if (pathname.startsWith("/top"))           return "/top";
  if (pathname.startsWith("/ip"))            return "/ip";
  if (pathname.startsWith("/storage"))       return "/storage";
  if (pathname.startsWith("/users"))         return "/users";
  if (pathname.startsWith("/notifications")) return "/notifications";
  if (pathname.startsWith("/settings"))     return "/settings";
  if (pathname.startsWith("/report-history")) return "/report-history";
  if (pathname.startsWith("/reports"))      return "/reports";
  return "/";
}

// RequireRole guards a route: a user below `need` is bounced to the
// dashboard instead of loading an admin/operator-only page (which would
// 403 its API calls and could leak system config). Defense-in-depth on
// top of the nav hiding — covers manual URL navigation.
function RequireRole({ need, children }: { need: "operator" | "admin"; children: React.ReactNode }) {
  const { isAdmin, isOperator } = useRole();
  const ok = need === "admin" ? isAdmin : isOperator;
  if (!ok) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// SensorDashboardRoute renders the shared Dashboard scoped to one sFlow
// source — the :id path param selects the sensor.
function SensorDashboardRoute() {
  const { id } = useParams();
  return <Dashboard sensorId={Number(id)} />;
}

// SensorsDashboardNav renders the main "Sensors Dashboard" nav item with a
// collapsible list of per-sFlow-source drill-downs nested under it. Clicking
// the label navigates to the all-sources dashboard; clicking the ▾/▸ caret
// expands/collapses the per-source children (collapse state persisted so the
// sidebar stays tidy when there are many sensors). Each child opens the same
// dashboard scoped to that one source.
const SRC_OPEN_KEY = "sflow.sidebar.sources_open";

function SensorsDashboardNav({
  item,
  active,
  onNav,
}: {
  item: NavItem;
  active: boolean;
  onNav?: () => void;
}) {
  const loc = useLocation();
  const [sensors, setSensors] = useState<{ id: number; name: string }[]>([]);
  const [open, setOpen] = useState(() => localStorage.getItem(SRC_OPEN_KEY) !== "0");
  useEffect(() => {
    api.sensors()
      .then((s) => setSensors(s.map((x) => ({ id: x.id, name: x.name }))))
      .catch(() => {});
  }, []);
  const toggle = () =>
    setOpen((o) => {
      localStorage.setItem(SRC_OPEN_KEY, o ? "0" : "1");
      return !o;
    });
  const hasChildren = sensors.length > 0;
  return (
    <>
      <div style={{ display: "flex", alignItems: "stretch" }}>
        <Link
          to={item.to}
          className={`sflow-nav-item${active ? " active" : ""}`}
          onClick={onNav}
          style={{ flex: 1, minWidth: 0 }}
        >
          <span className="sflow-nav-icon">{item.icon}</span>
          <span className="sflow-nav-label">{item.label}</span>
        </Link>
        {hasChildren && (
          <button
            type="button"
            aria-label={open ? "Collapse sources" : "Expand sources"}
            onClick={toggle}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--muted)",
              fontSize: 10,
              padding: "0 12px",
            }}
          >
            {open ? "▾" : "▸"}
          </button>
        )}
      </div>
      {hasChildren &&
        open &&
        sensors.map((s) => {
          const to = `/sensors/${s.id}/dashboard`;
          return (
            <Link
              key={s.id}
              to={to}
              className={`sflow-nav-item${loc.pathname === to ? " active" : ""}`}
              onClick={onNav}
              style={{ paddingLeft: 30, fontSize: 12.5 }}
            >
              <span
                className="sflow-nav-icon"
                style={{ opacity: 0.6, fontSize: 11, color: "var(--muted)" }}
              >
                ›
              </span>
              <span className="sflow-nav-label">{s.name}</span>
            </Link>
          );
        })}
    </>
  );
}

// ── sidebar content ───────────────────────────────────────────────────────

function SidebarContent({ onNav }: { onNav?: () => void }) {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { isAdmin, isOperator } = useRole();
  const current = activeKey(loc.pathname);

  // Hide nav items the current role may not use — readonly never sees the
  // Settings/Users/Storage links that would 403 or leak system config.
  const canSee = (item: NavItem) =>
    item.minRole === "admin" ? isAdmin :
    item.minRole === "operator" ? isOperator :
    true;

  async function handleLogout() {
    await logout();
    nav("/login", { replace: true });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>

      {/* Nav sections */}
      <nav className="sflow-nav">
        {NAV.map((section) => {
          const items = section.items.filter(canSee);
          if (items.length === 0) return null;
          return (
          <div key={section.title} className="sflow-nav-section">
            <span className="sflow-nav-section-title">{section.title}</span>

            {items.map((item) => {
              // The main Sensors Dashboard gets a collapsible per-source
              // drill-down nested beneath it.
              if (item.key === "/") {
                return (
                  <SensorsDashboardNav
                    key={item.key}
                    item={item}
                    active={current === item.key}
                    onNav={onNav}
                  />
                );
              }
              return item.disabled ? (
                <span
                  key={item.key}
                  className="sflow-nav-item"
                  style={{ opacity: 0.35, cursor: "not-allowed" }}
                >
                  <span className="sflow-nav-icon">{item.icon}</span>
                  <span className="sflow-nav-label">{item.label}</span>
                </span>
              ) : (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`sflow-nav-item${current === item.key ? " active" : ""}`}
                  onClick={onNav}
                >
                  <span className="sflow-nav-icon">{item.icon}</span>
                  <span className="sflow-nav-label">{item.label}</span>
                </Link>
              );
            })}

            {/* Wansight-style IP Addresses + IP Groups panels, right
                under the Network section. */}
            {section.title === "Network" && (
              <SidebarIPBrowser onNav={onNav} />
            )}
          </div>
          );
        })}
      </nav>

      {/* User footer */}
      {user && (
        <div className="sflow-sidebar-footer">
          <div className="sflow-user-avatar" style={{ width: 28, height: 28, fontSize: 12, flexShrink: 0 }}>
            {user.username[0].toUpperCase()}
          </div>
          <div className="sflow-sidebar-footer-info">
            <div className="sflow-sidebar-footer-name">{user.username}</div>
            <div className="sflow-sidebar-footer-role">{user.role}</div>
          </div>
          <button
            onClick={handleLogout}
            title="Logout"
            className="sflow-sidebar-logout"
            aria-label="Logout"
          >
            <LogoutOutlined />
          </button>
        </div>
      )}
    </div>
  );
}

// ── shell ─────────────────────────────────────────────────────────────────

const UPDATE_POLL_INTERVAL = 60 * 60 * 1000; // 1 hour

// ThemeToggleButton cycles light → dark → auto. The icon reflects the
// current MODE (not the resolved effective theme): sun for light, moon
// for dark, monitor for auto. Tooltip names the next mode the user gets
// on click so the action is obvious.
function ThemeToggleButton() {
  const { mode, cycle } = useTheme();
  const next: Record<ThemeMode, string> = {
    light: "Dark",
    dark:  "Auto (system)",
    auto:  "Light",
  };
  const icon =
    mode === "light" ? <BulbOutlined /> :
    mode === "dark"  ? <MoonOutlined /> :
                       <DesktopOutlined />;
  const label =
    mode === "light" ? "Light" :
    mode === "dark"  ? "Dark" :
                       "Auto";
  return (
    <Tooltip title={`Theme: ${label} — click for ${next[mode]}`}>
      <button
        onClick={cycle}
        className="sflow-theme-toggle"
        aria-label={`Theme: ${label}`}
      >
        {icon}
      </button>
    </Tooltip>
  );
}

function Shell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  async function handleShellLogout() {
    await logout();
    nav("/login", { replace: true });
  }
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [hasUpdate, setHasUpdate] = useState(false);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // Poll for updates every hour (also on mount)
  useEffect(() => {
    const checkUpdate = async () => {
      try {
        const result = await api.updateCheck();
        setHasUpdate(result.has_update);
      } catch {
        // mirror unreachable — silently ignore
      }
    };
    checkUpdate();
    pollTimerRef.current = setInterval(checkUpdate, UPDATE_POLL_INTERVAL);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  return (
    <Layout style={{ minHeight: "100vh" }}>

      {/* ── Top header ─────────────────────────────────────────────────── */}
      <Layout.Header className="sflow-header">
        {isMobile && (
          <button className="sflow-hamburger" onClick={() => setDrawerOpen(true)}>
            <MenuOutlined />
          </button>
        )}

        <span className="sflow-brand">
          <span className="sflow-brand-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#001018" strokeWidth="2.2" strokeLinecap="round">
              <path d="M2 12h4l3-7 4 14 3-7h6"/>
            </svg>
          </span>
          <span className="sflow-brand-text">NetScope Console</span>
        </span>

        <div className="sflow-header-spacer" />

        {/* Update available badge */}
        {hasUpdate && (
          <Tooltip title="Update available — click to view">
            <button
              onClick={() => nav("/settings")}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: 6,
                color: "#60a5fa",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
              }}
            >
              <Badge dot>
                <CloudDownloadOutlined style={{ fontSize: 16 }} />
              </Badge>
              {!isMobile && <span style={{ marginLeft: 6 }}>Update</span>}
            </button>
          </Tooltip>
        )}

        <ThemeToggleButton />

        {user && !isMobile && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div className="sflow-user-chip">
              <div className="sflow-user-avatar">
                {user.username[0].toUpperCase()}
              </div>
              <span className="sflow-user-name">{user.username}</span>
              <span className="sflow-user-role">{user.role}</span>
            </div>
            <Tooltip title="Logout">
              <button
                onClick={handleShellLogout}
                className="sflow-logout-btn"
                aria-label="Logout"
              >
                <LogoutOutlined />
                <span className="sflow-logout-text">Logout</span>
              </button>
            </Tooltip>
          </div>
        )}

        {user && isMobile && (
          <Tooltip title="Logout">
            <button
              onClick={handleShellLogout}
              className="sflow-logout-btn"
              aria-label="Logout"
            >
              <LogoutOutlined />
            </button>
          </Tooltip>
        )}
      </Layout.Header>

      <Layout style={{ flex: 1, minHeight: 0 }}>

        {/* ── Desktop sidebar ─────────────────────────────────────────── */}
        {!isMobile && (
          <Layout.Sider
            className="sflow-sider"
            width={220}
            style={{
              background: "var(--bg-0)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 48px)",
              position: "sticky",
              top: 48,
              alignSelf: "flex-start",
            }}
          >
            <SidebarContent />
          </Layout.Sider>
        )}

        {/* ── Mobile drawer ───────────────────────────────────────────── */}
        {isMobile && (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            placement="left"
            width={230}
            className="sflow-mobile-drawer"
            styles={{
              body:    { padding: 0, background: "var(--bg-0)", display: "flex", flexDirection: "column" },
              header:  { display: "none" },
              content: { background: "var(--bg-0)" },
              wrapper: { background: "transparent" },
              mask:    { backdropFilter: "blur(3px)" },
            }}
          >
            <SidebarContent onNav={() => setDrawerOpen(false)} />
          </Drawer>
        )}

        {/* ── Main content ────────────────────────────────────────────── */}
        <Layout style={{ background: "var(--bg-0)", minWidth: 0 }}>
          <Layout.Content style={{ padding: isMobile ? 12 : 20, minHeight: "calc(100vh - 48px)" }}>
            <Routes>
              <Route path="/"             element={<Dashboard />}      />
              <Route path="/sensors"      element={<SFlowSources />}   />
              <Route path="/sensors/:id/dashboard" element={<SensorDashboardRoute />} />
              <Route path="/sensors/:id"  element={<SensorDetail />}   />
              <Route path="/zones"        element={<ZoneEditor />}      />
              <Route path="/decoders"     element={<Decoders />}        />
              <Route path="/storage"      element={<RequireRole need="operator"><GraphsStorage /></RequireRole>} />
              <Route path="/users"        element={<RequireRole need="admin"><UserManagement /></RequireRole>} />
              <Route path="/flows"        element={<Flows />}          />
              <Route path="/flows/search" element={<FlowsArchive />}   />
              <Route path="/ip"           element={<IPAddresses />}    />
              <Route path="/top"          element={<IPTop />}          />
              <Route path="/notifications" element={<RequireRole need="operator"><Notifications /></RequireRole>} />
              <Route path="/settings"     element={<RequireRole need="admin"><Settings /></RequireRole>} />
              <Route path="/reports"        element={<RequireRole need="operator"><Reports /></RequireRole>} />
              <Route path="/report-history" element={<RequireRole need="operator"><ReportHistory /></RequireRole>} />
            </Routes>
          </Layout.Content>

          <Layout.Footer className="sflow-footer">
            <span>NetScope Console v0.1</span>
            <span style={{ color: "#cbd5e1" }}>{window.location.host}</span>
          </Layout.Footer>
        </Layout>

      </Layout>
    </Layout>
  );
}

// ── root ──────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <LicenseGate>
                <IPZoneProvider>
                  <Shell />
                </IPZoneProvider>
              </LicenseGate>
            </RequireAuth>
          }
        />
      </Routes>
    </AuthProvider>
  );
}
